/*  HomeStoriesRibbon — Instagram-style sticky ribbon of round profile
    avatars with a coloured ring around anyone who has an unseen story.

    Fixed at the top of the Home content, sits right under the mobile
    top-bar and stays visible while the user scrolls the AI hero. Tap on
    an avatar → opens that user's stories in the full-screen viewer.

    Data:
      - GET /api/stories/feed?scope=nearby returns fresh (non-expired)
        stories with embedded `author`; we group by user_id, sort by
        distance (already server-side), and render one avatar per author.
      - Unseen state is tracked client-side in localStorage against
        the story id — same technique used by WithStoryRing.
*/
import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";

const SEEN_KEY = "orio.stories.seen.v1";

function readSeen() {
  try { return JSON.parse(localStorage.getItem(SEEN_KEY) || "{}"); } catch { return {}; }
}

export default function HomeStoriesRibbon() {
  const [items, setItems] = useState([]);
  const [seen, setSeen] = useState(readSeen());
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Nearby-first with a national fallback so the ribbon isn't
        // empty for users whose radius has nothing fresh right now.
        let list = [];
        try {
          const { data } = await api.get("/stories/feed?scope=nearby&limit=40");
          list = data?.stories || [];
        } catch { /* silent */ }
        if (list.length === 0) {
          const { data } = await api.get("/stories/feed?scope=national&limit=40");
          list = data?.stories || [];
        }
        if (cancelled) return;
        // Group by user_id, keep the freshest story per author for the
        // avatar's story-id target.
        const byUser = new Map();
        for (const s of list) {
          if (!s.user_id || !s.author) continue;
          if (!byUser.has(s.user_id)) byUser.set(s.user_id, s);
        }
        setItems(Array.from(byUser.values()));
      } catch { /* silent — feed empties are fine */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Refresh seen state whenever we come back to the page (e.g. after
  // viewing a story elsewhere and returning here).
  useEffect(() => {
    const on = () => setSeen(readSeen());
    window.addEventListener("focus", on);
    document.addEventListener("visibilitychange", on);
    return () => {
      window.removeEventListener("focus", on);
      document.removeEventListener("visibilitychange", on);
    };
  }, []);

  const rings = useMemo(() => items.map((s) => ({
    ...s,
    unseen: !seen[s.id],
  })), [items, seen]);

  if (rings.length === 0) return null;

  return (
    <div
      className="w-full"
      data-testid="home-stories-ribbon"
      style={{
        // Fade smoothly INTO the OrioAIHero warm gradient below: start
        // slightly cream at top, end fully transparent so the seam with the
        // hero disappears. No hard border — a soft glow instead.
        background:
          "linear-gradient(180deg, rgba(251,247,239,0.70) 0%, " +
          "rgba(252,239,221,0.35) 60%, rgba(255,225,190,0) 100%)",
        backdropFilter: "blur(14px) saturate(140%)",
        WebkitBackdropFilter: "blur(14px) saturate(140%)",
        borderBottom: "none",
        boxShadow: "0 12px 24px -18px rgba(255,122,26,0.20)",
      }}
    >
      <div className="flex items-center gap-3 px-4 py-2 overflow-x-auto orio-h-scroll">
        {rings.map((s, i) => {
          const author = s.author || {};
          const src = author.photo_url;
          return (
            <motion.button
              key={s.user_id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.24, delay: Math.min(i * 0.03, 0.2) }}
              onClick={() => {
                // Optimistically mark this user's story as seen (the ring
                // fades from coloured to grey once you open their profile).
                const next = { ...seen, [s.id]: 1 };
                try { localStorage.setItem(SEEN_KEY, JSON.stringify(next)); } catch { /* silent */ }
                setSeen(next);
                // Primary tap → open the person's profile (Tinder-style
                // carousel). From the profile they can tap "Storia in
                // corso" to jump into the story viewer.
                navigate(`/app/u/${encodeURIComponent(s.user_id)}`);
              }}
              className="shrink-0 flex flex-col items-center active:scale-95"
              data-testid={`ribbon-story-${s.user_id}`}
              aria-label={`Storia di ${author.name || "utente"}`}
            >
              <span
                className="relative inline-flex items-center justify-center rounded-full"
                style={{
                  padding: 2.5,
                  background: s.unseen
                    ? "conic-gradient(from 210deg, #FF7A1A, #F4B83C, #E9542A, #FF7A1A)"
                    : "linear-gradient(135deg, rgba(0,0,0,0.10), rgba(0,0,0,0.06))",
                }}
              >
                <span
                  className="block rounded-full overflow-hidden bg-white flex items-center justify-center"
                  style={{ width: 58, height: 58, padding: 2 }}
                >
                  {src ? (
                    <img src={src} alt="" className="w-full h-full rounded-full object-cover" loading="lazy" />
                  ) : (
                    <span
                      className="w-full h-full rounded-full flex items-center justify-center text-white font-display text-sm"
                      style={{ background: "linear-gradient(135deg,#FF7A1A,#F4B83C)" }}
                    >
                      {(author.name || "?").trim().charAt(0).toUpperCase()}
                    </span>
                  )}
                </span>
              </span>
              <span
                className="mt-1 text-[10.5px] leading-none max-w-[64px] truncate text-[#3A3A3A] font-medium"
                title={author.name}
              >
                {(author.name || "").split(" ")[0]}
              </span>
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}
